<template>
  <MobileAuthLayout v-slot="{ facilityId }" @auth-ready="onAuthReady">
    <!-- Method C: type-grouped flow (mockup-spec). Parent owns task fetch
         and auth; child takes over rendering once task is loaded. -->
    <PageMobilePreInstallMethodC
      v-if="task && isMethodC"
      :task="task"
      :facility-id="facilityId"
      @task-updated="onMethodCTaskUpdated"
    />
    <div v-else class="mobile-preinstall">
      <!-- ── ASSIGN SCREEN ── -->
      <div v-if="currentScreen === 'assign'" class="screen-container">
        <div class="task-header">
          <h2 class="task-title">{{ taskTitle }}</h2>
          <div class="task-meta">
            <span class="due-date">Due: {{ formattedDueDate }}</span>
          </div>
          <div v-if="task && task.description" class="task-description">
            {{ task.description }}
          </div>
        </div>

        <div v-if="error" class="error-container">
          <v-alert type="error" outlined>
            {{ error }}
            <template #append>
              <v-btn text small @click="loadTask(facilityId)">Retry</v-btn>
            </template>
          </v-alert>
        </div>

        <div v-else-if="loading" class="loading-container">
          <v-skeleton-loader
            v-for="n in 4"
            :key="n"
            type="card"
            height="90"
            class="mb-3"
          />
        </div>

        <div v-else-if="task" class="assignments-container">
          <!-- Progress header -->
          <div class="progress-header">
            <div class="progress-info">
              <span class="progress-label">Device to Unit Assignments</span>
              <span class="progress-text">
                {{ completedAssignments }}/{{ targetCount }} assigned
              </span>
            </div>
            <v-progress-linear
              :value="progressPercentage"
              color="success"
              height="6"
              rounded
              class="mt-2"
            />
          </div>

          <!-- Assignment cards -->
          <div class="assignment-cards">
            <div
              v-for="(assignment, index) in deviceAssignments"
              :key="assignment.id"
              class="assignment-card"
              :class="{
                'card-completed':
                  assignment.locallyAssigned || assignment.completed,
              }"
            >
              <div class="card-header">
                <span class="card-number">{{ index + 1 }}</span>
                <v-chip
                  v-if="assignment.locallyAssigned"
                  x-small
                  color="success"
                  dark
                >
                  Assigned
                </v-chip>
                <v-chip
                  v-else-if="assignment.completed"
                  x-small
                  color="blue"
                  dark
                >
                  Saved
                </v-chip>
                <v-btn
                  v-if="
                    !(assignment.locallyAssigned || assignment.completed) &&
                    deviceAssignments.length > 1
                  "
                  icon
                  x-small
                  color="error"
                  class="ml-auto"
                  @click="removeAssignment(index)"
                >
                  <v-icon small>mdi-close-circle-outline</v-icon>
                </v-btn>
              </div>

              <!-- Completed/saved display -->
              <div
                v-if="assignment.locallyAssigned || assignment.completed"
                class="assignment-display"
              >
                <div class="assignment-pair">
                  <div class="pair-item">
                    <v-icon small color="primary" class="mr-1"
                      >mdi-package-variant</v-icon
                    >
                    <span class="pair-label">Device:</span>
                    <span class="pair-value">
                      {{
                        assignment.selectedDevice
                          ? formatDeviceName(assignment.selectedDevice)
                          : "—"
                      }}
                    </span>
                  </div>
                  <v-icon small color="grey" class="arrow-icon"
                    >mdi-arrow-right</v-icon
                  >
                  <div class="pair-item">
                    <v-icon small color="success" class="mr-1"
                      >mdi-garage-variant</v-icon
                    >
                    <span class="pair-label">Unit:</span>
                    <span class="pair-value">
                      {{
                        assignment.selectedUnit
                          ? assignment.selectedUnit.name
                          : assignment.selectedUnitId || "—"
                      }}
                    </span>
                  </div>
                </div>
                <div v-if="assignment.comment" class="assignment-comment">
                  <v-icon x-small color="grey" class="mr-1"
                    >mdi-comment-outline</v-icon
                  >
                  <em>{{ assignment.comment }}</em>
                </div>
              </div>

              <!-- Editable state -->
              <div v-else class="assignment-inputs">
                <v-autocomplete
                  v-model="assignment.pendingUnitId"
                  :items="unitsForAssignment()"
                  :item-text="(u) => u.name"
                  item-value="id"
                  label="Select Unit"
                  dense
                  outlined
                  hide-details
                  clearable
                  class="mb-2"
                  @change="onUnitChanged(assignment, $event)"
                >
                  <template #item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle
                        v-if="unitTypeName(item)"
                        class="text-caption"
                      >
                        {{ unitTypeName(item) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>

                <v-btn
                  color="primary"
                  small
                  block
                  outlined
                  class="mb-2"
                  @click="openScanner(index)"
                >
                  <v-icon left small>mdi-qrcode-scan</v-icon>
                  Scan Device
                </v-btn>

                <div class="d-flex align-center mb-2">
                  <v-divider />
                  <span class="mx-2 caption grey--text">OR</span>
                  <v-divider />
                </div>

                <v-autocomplete
                  v-model="assignment.selectedDeviceId"
                  :items="availableDevicesForAssignment(assignment)"
                  :item-text="(d) => formatDeviceName(d)"
                  item-value="id"
                  label="Select Device"
                  dense
                  outlined
                  hide-details
                  clearable
                  class="mb-2"
                  @change="updateDeviceSelection(assignment)"
                />

                <v-btn
                  color="success"
                  small
                  block
                  :disabled="
                    !assignment.selectedDevice || !assignment.pendingUnitId
                  "
                  @click="locallyAssignDevice(assignment)"
                >
                  <v-icon small left>mdi-link-variant</v-icon>
                  Assign
                </v-btn>
              </div>

              <!-- Comment field for assigned rows -->
              <div
                v-if="
                  (assignment.locallyAssigned || assignment.completed) &&
                  !assignment.completed
                "
                class="comment-row"
              >
                <v-text-field
                  v-model="assignment.comment"
                  placeholder="Add comment (optional)"
                  dense
                  outlined
                  hide-details
                  prepend-inner-icon="mdi-comment-outline"
                />
              </div>
            </div>
          </div>

          <!-- Add assignment button -->
          <div class="add-row-section">
            <v-btn
              outlined
              color="primary"
              block
              small
              :disabled="!canAddMoreAssignments"
              @click="addAssignment"
            >
              <v-icon left small>mdi-plus</v-icon>
              Add Assignment
            </v-btn>
            <p
              v-if="deviceAssignments.length >= maxAssignments"
              class="caption grey--text text-center mt-1"
            >
              Maximum {{ maxAssignments }} assignments reached
            </p>
          </div>

          <!-- Action button -->
          <div class="action-container">
            <v-btn
              color="success"
              large
              block
              rounded
              :disabled="!canCompleteTask"
              :loading="completing"
              @click="completePreInstallTask"
            >
              <v-icon left>mdi-check-circle</v-icon>
              {{
                task.resolution === "done"
                  ? "Save Additional Assignments"
                  : "Complete Pre-Install Task"
              }}
            </v-btn>
          </div>
        </div>
      </div>

      <!-- ── SUMMARY SCREEN ── -->
      <div v-if="currentScreen === 'summary'" class="screen-container">
        <div class="completion-header">
          <v-icon size="72" color="success">mdi-check-circle</v-icon>
          <h2>Pre-Install Task Completed!</h2>
          <p class="completion-subtitle">
            {{ summaryAssignments.length }} device(s) assigned successfully
          </p>
        </div>

        <div class="completion-summary pa-4">
          <!-- Assignment list -->
          <v-card outlined class="mb-4">
            <v-card-title class="subtitle-2 py-2">
              Assignment Summary
            </v-card-title>
            <v-divider />
            <v-list dense>
              <v-list-item
                v-for="(item, idx) in summaryAssignments"
                :key="idx"
                class="py-1"
              >
                <v-list-item-content>
                  <v-list-item-title class="body-2">
                    <span class="font-weight-medium">{{ item.unitName }}</span>
                  </v-list-item-title>
                  <v-list-item-subtitle>{{
                    item.deviceName
                  }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon small color="success">mdi-check</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>

          <!-- Email notification -->
          <v-card outlined class="mb-4">
            <v-card-title class="subtitle-2 py-2">
              Send Notification (optional)
            </v-card-title>
            <v-divider />
            <div class="pa-3">
              <v-text-field
                v-model="emailInput"
                label="Email address"
                placeholder="example@company.com"
                outlined
                dense
                hide-details
                class="mb-2"
                @keyup.enter="addEmail"
              >
                <template #append>
                  <v-btn
                    small
                    color="primary"
                    :disabled="!isValidEmail(emailInput)"
                    @click="addEmail"
                  >
                    Add
                  </v-btn>
                </template>
              </v-text-field>

              <div v-if="emailList.length > 0" class="mb-2">
                <v-chip
                  v-for="(email, i) in emailList"
                  :key="i"
                  close
                  small
                  class="mr-1 mb-1"
                  @click:close="removeEmail(i)"
                >
                  {{ email }}
                </v-chip>
              </div>

              <v-text-field
                v-model="doneByInput"
                label="Assigned by"
                placeholder="Your name"
                outlined
                dense
                hide-details
                class="mb-3"
              />

              <v-btn
                color="primary"
                block
                :disabled="!canSendNotification"
                :loading="sendingNotification"
                @click="sendSummaryNotification"
              >
                <v-icon left>mdi-email-send</v-icon>
                Send Notification
              </v-btn>
            </div>
          </v-card>

          <v-btn color="success" large block rounded @click="finishAndClose">
            <v-icon left>mdi-check</v-icon>
            Finish
          </v-btn>
        </div>
      </div>

      <!-- ── QR SCANNER OVERLAY ── -->
      <div v-if="scannerOpen" class="scanner-overlay">
        <div class="scanner-header">
          <v-btn icon @click="closeScanner">
            <v-icon color="white">mdi-arrow-left</v-icon>
          </v-btn>
          <span>Scan Device QR Code</span>
        </div>

        <div class="scanner-body">
          <QRCodeScanner
            ref="qrScanner"
            :advanced="true"
            @detect-device="onDeviceScanned"
            @detect="onQRDetected"
          />
        </div>

        <div class="scanner-manual-entry pa-3">
          <v-text-field
            v-model="manualScanDeviceId"
            label="Or enter Device ID manually"
            placeholder="e.g. CF00510"
            outlined
            dense
            hide-details
            dark
            class="mb-2"
            @keyup.enter="searchManualScanDevice"
          />
          <v-btn outlined block color="white" @click="searchManualScanDevice">
            Search by ID
          </v-btn>
        </div>
      </div>

      <v-snackbar
        v-model="showSnackbar"
        :color="snackbarColor"
        :timeout="snackbarTimeout"
        bottom
      >
        {{ snackbarMessage }}
        <template #action="{ attrs }">
          <v-btn text v-bind="attrs" @click="showSnackbar = false">Close</v-btn>
        </template>
      </v-snackbar>
    </div>
  </MobileAuthLayout>
</template>

<script>
import MobileAuthLayout from "../../mobile/MobileAuthLayout.vue";
import QRCodeScanner from "@tod-ui/components/QRCodeScanner.vue";
import PageMobilePreInstallMethodC from "./PageMobilePreInstallMethodC.vue";
import { GET_PREINSTALL_TASK, UPDATE_TASK } from "../graphql";
import {
  OFFICE_INVENTORY_DEVICES,
  PREINSTALLED_INVENTORY_UNITS,
  SEND_PREINSTALLED_DEVICE_MOBILE_SUMMARY,
} from "@components/smart_units/graphql";
import { addOrganizationDevices } from "../../../graphql/organizations/index";
import { formatDateSlashUTC } from "@tod-ui/helpers/datetime";
import { formatDeviceName } from "@components/common/helpers/devices";

export default {
  name: "PageMobilePreInstallThreshold",
  components: {
    MobileAuthLayout,
    QRCodeScanner,
    PageMobilePreInstallMethodC,
  },
  data() {
    return {
      currentScreen: "assign",
      task: null,
      loading: false,
      completing: false,
      error: "",
      // Device / unit lists
      availableDevices: [],
      vacantUnits: [],
      devicesLoading: false,
      unitsLoading: false,
      // Assignment rows
      deviceAssignments: [],
      // Summary
      summaryAssignments: [],
      showSummary: false,
      // Email notification
      emailInput: "",
      emailList: [],
      doneByInput: "",
      sendingNotification: false,
      // Snackbar
      showSnackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      snackbarTimeout: 4000,
      // QR scanner
      scannerOpen: false,
      scanningForIndex: null,
      manualScanDeviceId: "",
    };
  },

  computed: {
    taskId() {
      return this.$route.params.taskId;
    },
    taskTitle() {
      return this.task?.title || "Pre-Install Task";
    },
    formattedDueDate() {
      if (!this.task?.dueAt) return "";
      return formatDateSlashUTC(this.task.dueAt);
    },
    resolutionColor() {
      const r = this.task?.resolution;
      if (r === "done") return "success";
      if (r === "in_progress") return "orange";
      return "grey";
    },
    resolutionLabel() {
      const r = this.task?.resolution;
      if (r === "done") return "Done";
      if (r === "in_progress") return "In Progress";
      return r || "";
    },

    // ── Target count (mirrors DialogPreInstallModal.targetCount) ──
    targetCount() {
      const serverTarget = this.task?.actions?.targetCount;
      if (serverTarget && serverTarget > 0) return serverTarget;

      const c = this.task?.taskRule?.conditions;
      if (!c || !c.method) {
        const checklistLen = Array.isArray(this.task?.actions?.checklist)
          ? this.task.actions.checklist.length
          : 0;
        return Math.max(checklistLen, 1);
      }
      if (c.method === "vacancy_threshold") {
        return c.targetAtOrAboveThreshold || c.targetBelowThreshold || 1;
      }
      if (c.method === "high_penetration") return c.fixedTargetUnits || 1;
      if (c.method === "unit_type_coverage") return c.devicesPerUnit || 1;
      return 1;
    },

    maxAssignments() {
      return this.targetCount * 3;
    },

    preInstallMethod() {
      return (
        this.task?.actions?.preInstallMethod ||
        this.task?.taskRule?.conditions?.method ||
        null
      );
    },

    // Method C uses a separate page (type-grouped flow). The parent still
    // handles auth + task fetch — child renders once the task is loaded.
    isMethodC() {
      return this.preInstallMethod === "unit_type_coverage";
    },

    unitTypeRanking() {
      return this.task?.actions?.unitTypeRanking || [];
    },

    completedAssignments() {
      return this.deviceAssignments.filter(
        (a) => a.locallyAssigned || a.completed
      ).length;
    },

    progressPercentage() {
      return this.targetCount > 0
        ? (this.completedAssignments / this.targetCount) * 100
        : 0;
    },

    canCompleteTask() {
      return this.deviceAssignments.some((a) => a.locallyAssigned);
    },

    canAddMoreAssignments() {
      return (
        this.availableDevices.length > 0 &&
        this.vacantUnits.length > 0 &&
        this.deviceAssignments.length < this.maxAssignments
      );
    },

    canSendNotification() {
      return this.emailList.length > 0 && this.doneByInput.trim().length > 0;
    },

    lockedDeviceIds() {
      const ids = new Set();
      this.deviceAssignments.forEach((a) => {
        if (!(a.locallyAssigned || a.completed)) return;
        if (a.selectedDeviceId) ids.add(a.selectedDeviceId);
        if (a.selectedDevice?.id) ids.add(a.selectedDevice.id);
      });
      return ids;
    },
  },

  methods: {
    onAuthReady(facilityId) {
      this.loadTask(facilityId);
    },

    // Method C child completed/finished — refetch so the page reflects the
    // saved state (resolution flips to "done", assignmentsByType populated).
    async onMethodCTaskUpdated() {
      const fid =
        this.task && this.task.organization && this.task.organization.id;
      await this.loadTask(fid);
    },

    // ── Data loading ──────────────────────────────────────────────────────────

    async loadTask(facilityId) {
      this.loading = true;
      this.error = "";
      try {
        const result = await this.$apollo.query({
          query: GET_PREINSTALL_TASK,
          variables: { id: this.taskId },
          fetchPolicy: "network-only",
        });

        if (!result.data?.findTask) throw new Error("Task not found");
        this.task = result.data.findTask;

        if (!this.isMethodC) {
          const fId = facilityId || this.task.organization?.id;
          await Promise.all([this.loadDevices(fId), this.loadVacantUnits(fId)]);
          this.initializeAssignments();
        }
      } catch (err) {
        console.error("Error loading pre-install task:", err);
        this.error = err.message || "Failed to load task. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    async loadDevices(facilityId) {
      if (!facilityId) return;
      this.devicesLoading = true;
      try {
        const result = await this.$apollo.query({
          query: OFFICE_INVENTORY_DEVICES,
          variables: {
            id: facilityId,
            availableInventory: true,
            dispositions: ["in_service", "waiting"],
            statuses: ["ok"],
            organizationAssigned: false,
            limit: 200,
            page: 1,
          },
          fetchPolicy: "network-only",
        });
        const data = result.data?.organizationDevicesPaginated?.data || [];
        this.availableDevices = data.filter(
          (d) =>
            d.disposition === "in_service" && d.status === "ok" && !d.rmaRequest
        );
      } catch (err) {
        console.error("Error loading devices:", err);
      } finally {
        this.devicesLoading = false;
      }
    },

    async loadVacantUnits(facilityId) {
      if (!facilityId) return;
      this.unitsLoading = true;
      try {
        const sort =
          this.preInstallMethod === "vacancy_threshold"
            ? "vacancy_duration asc"
            : "name asc";

        const result = await this.$apollo.query({
          query: PREINSTALLED_INVENTORY_UNITS,
          variables: {
            id: facilityId,
            assigned: false,
            deviceAssigned: false,
            type: "zone",
            subtype: "unit",
            limit: 500,
            page: 1,
            sort,
          },
          fetchPolicy: "network-only",
        });
        const data = result.data?.organizationDescendantsPaginated;
        this.vacantUnits = (data && data.data) || [];
      } catch (err) {
        console.error("Error loading vacant units:", err);
      } finally {
        this.unitsLoading = false;
      }
    },

    // ── Assignment row initialisation (mirrors DialogPreInstallModal) ─────────

    initializeAssignments() {
      this.deviceAssignments = [];
      this.showSummary = false;

      this.summaryAssignments = [];
      this.clearLocalStorage();

      const hasSavedAssignments =
        Array.isArray(this.task?.actions?.assignments) &&
        this.task.actions.assignments.length > 0;

      if (hasSavedAssignments) {
        this.populateExistingAssignments();
        this.autoFillRowsByMethod();
        return;
      }

      const restored = this.restoreFromLocalStorage();
      if (restored) {
        this.ensureMinimumRows();
        return;
      }

      this.populateExistingAssignments();
      this.autoFillRowsByMethod();
    },

    populateExistingAssignments() {
      const actions = this.task?.actions;

      const savedAssignments =
        actions && Array.isArray(actions.assignments)
          ? actions.assignments
          : null;

      if (savedAssignments && savedAssignments.length > 0) {
        this.deviceAssignments = savedAssignments.map((item, idx) => ({
          id: `restored-${idx}`,
          selectedDeviceId: null,
          selectedUnitId: null,
          selectedDevice:
            item.device_name || item.deviceName
              ? { id: null, name: item.device_name || item.deviceName }
              : null,
          selectedUnit:
            item.unit_name || item.unitName
              ? { id: null, name: item.unit_name || item.unitName }
              : null,
          pendingUnitId: null,
          locallyAssigned: false,
          completed: true,
          comment: item.comment || "",
          unitTypeFilter: null,
        }));
        return;
      }

      const checklist = Array.isArray(actions?.checklist)
        ? actions.checklist
        : [];
      this.deviceAssignments = checklist.map((item) => {
        let selectedDevice = null;
        let selectedUnit = null;
        if (
          item.completed &&
          item.description &&
          item.description.includes(" → ")
        ) {
          const [deviceName, unitName] = item.description.split(" → ");
          selectedDevice = { id: null, name: deviceName.trim() };
          selectedUnit = { id: null, name: unitName.trim() };
        }
        return {
          id: item.id,
          selectedDeviceId: null,
          selectedUnitId: null,
          selectedDevice,
          selectedUnit,
          pendingUnitId: null,
          locallyAssigned: false,
          completed: Boolean(item.completed),
          comment: item.comment || "",
          unitTypeFilter: null,
        };
      });
    },

    autoFillRowsByMethod() {
      const method = this.preInstallMethod;
      if (method === "high_penetration") {
        this.fillRowsForHighPenetration();
      } else {
        while (this.deviceAssignments.length < this.targetCount) {
          this.deviceAssignments.push(this.newAssignmentRow());
        }
      }
    },

    fillRowsForHighPenetration() {
      const topType = this.unitTypeRanking[0] || null;
      while (this.deviceAssignments.length < this.targetCount) {
        this.deviceAssignments.push(this.newAssignmentRow(topType));
      }
    },

    ensureMinimumRows() {
      const method = this.preInstallMethod;
      const topType =
        method === "high_penetration" ? this.unitTypeRanking[0] || null : null;
      while (this.deviceAssignments.length < this.targetCount) {
        this.deviceAssignments.push(this.newAssignmentRow(topType));
      }
    },

    newAssignmentRow(unitTypeFilter = null) {
      return {
        id: `temp-${Date.now()}-${Math.random()}`,
        selectedDeviceId: null,
        selectedUnitId: null,
        selectedDevice: null,
        selectedUnit: null,
        pendingUnitId: null,
        locallyAssigned: false,
        completed: false,
        comment: "",
        unitTypeFilter,
      };
    },

    // ── Unit / device helpers ─────────────────────────────────────────────────

    unitsForAssignment() {
      const units = this.vacantUnits;
      const method = this.preInstallMethod;

      if (method === "high_penetration") {
        if (this.unitTypeRanking.length === 0) return units;
        const rankIndex = (unit) => {
          const typeName = this.unitTypeName(unit);
          const idx = this.unitTypeRanking.indexOf(typeName);
          return idx === -1 ? this.unitTypeRanking.length : idx;
        };
        return [...units].sort((a, b) => rankIndex(a) - rankIndex(b));
      }

      if (method === "unit_type_coverage") {
        return units.filter((unit) => {
          const typeName = (this.unitTypeName(unit) || "").toLowerCase();
          return !typeName.includes("parking");
        });
      }

      return units;
    },

    unitTypeName(unit) {
      if (!unit) return null;
      if (unit.unitType?.name) return unit.unitType.name;
      const match = unit.name && unit.name.match(/(\d+x\d+)/i);
      return match ? match[1] : null;
    },

    formatDeviceName(device) {
      return (
        device?.shortId || device?.externalId || `Device ${device?.id || ""}`
      );
    },

    availableDevicesForAssignment(currentAssignment) {
      const liveDevices = this.availableDevices;

      const extraDevices =
        currentAssignment.selectedDevice &&
        currentAssignment.selectedDeviceId &&
        !liveDevices.some((d) => d.id === currentAssignment.selectedDeviceId)
          ? [currentAssignment.selectedDevice]
          : [];

      const devicePool = [...extraDevices, ...liveDevices];

      return devicePool.filter((device) => {
        const deviceId = device.id;
        if (this.lockedDeviceIds.has(deviceId)) return false;
        if (
          currentAssignment.selectedDeviceId === deviceId ||
          currentAssignment.selectedDevice?.id === deviceId
        )
          return true;
        return !this.deviceAssignments.some(
          (a) =>
            a !== currentAssignment &&
            !(a.locallyAssigned || a.completed) &&
            (a.selectedDeviceId === deviceId ||
              a.selectedDevice?.id === deviceId)
        );
      });
    },

    // ── Row interaction ───────────────────────────────────────────────────────

    onUnitChanged(assignment, unitId) {
      assignment.pendingUnitId = unitId || null;
      if (!unitId) {
        assignment.selectedDeviceId = null;
        assignment.selectedDevice = null;
      }
    },

    updateDeviceSelection(assignment) {
      if (assignment.selectedDeviceId) {
        assignment.selectedDevice =
          this.availableDevices.find(
            (d) => d.id === assignment.selectedDeviceId
          ) || assignment.selectedDevice;
      } else {
        assignment.selectedDevice = null;
      }
      assignment.locallyAssigned = false;
      this.persistToLocalStorage();
    },

    locallyAssignDevice(assignment) {
      if (!assignment.selectedDevice || !assignment.pendingUnitId) {
        this.showErrorMessage(
          "Please select both a device and a unit before assigning."
        );
        return;
      }
      assignment.locallyAssigned = true;
      assignment.selectedUnitId = assignment.pendingUnitId;
      assignment.selectedUnit = this.vacantUnits.find(
        (u) => u.id === assignment.pendingUnitId
      );
      this.persistToLocalStorage();
    },

    addAssignment() {
      const method = this.preInstallMethod;
      const topType =
        method === "high_penetration" ? this.unitTypeRanking[0] || null : null;
      this.deviceAssignments.push(this.newAssignmentRow(topType));
      this.persistToLocalStorage();
    },

    removeAssignment(index) {
      this.deviceAssignments.splice(index, 1);
      this.persistToLocalStorage();
    },

    // ── localStorage ──────────────────────────────────────────────────────────

    localStorageKey() {
      return this.task ? `pre_install_task_${this.task.id}` : null;
    },

    persistToLocalStorage() {
      const key = this.localStorageKey();
      if (!key) return;
      const toSave = this.deviceAssignments.map((a) => ({
        id: a.id,
        selectedDeviceId: a.selectedDeviceId,
        selectedUnitId: a.selectedUnitId,
        selectedDeviceName: a.selectedDevice
          ? this.formatDeviceName(a.selectedDevice)
          : null,
        selectedUnitName: a.selectedUnit ? a.selectedUnit.name : null,
        locallyAssigned: a.locallyAssigned,
        completed: a.completed,
        comment: a.comment,
        unitTypeFilter: a.unitTypeFilter || null,
      }));
      try {
        localStorage.setItem(key, JSON.stringify(toSave));
      } catch (_e) {}
    },

    restoreFromLocalStorage() {
      const key = this.localStorageKey();
      if (!key) return false;
      let saved;
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return false;
        saved = JSON.parse(raw);
      } catch (_e) {
        return false;
      }
      if (!Array.isArray(saved) || saved.length === 0) return false;

      const restored = saved.map((item) => {
        const device = item.selectedDeviceId
          ? this.availableDevices.find((d) => d.id === item.selectedDeviceId)
          : null;
        const unit = item.selectedUnitId
          ? this.vacantUnits.find((u) => u.id === item.selectedUnitId)
          : null;
        return {
          id: item.id,
          selectedDeviceId: item.selectedDeviceId || null,
          selectedUnitId: item.selectedUnitId || null,
          selectedDevice:
            device ||
            (item.selectedDeviceName
              ? { id: item.selectedDeviceId, name: item.selectedDeviceName }
              : null),
          selectedUnit:
            unit ||
            (item.selectedUnitName
              ? { id: item.selectedUnitId, name: item.selectedUnitName }
              : null),
          pendingUnitId: item.locallyAssigned ? item.selectedUnitId : null,
          locallyAssigned: Boolean(item.locallyAssigned),
          completed: Boolean(item.completed),
          comment: item.comment || "",
          unitTypeFilter: item.unitTypeFilter || null,
        };
      });

      // Dedup locked device ids before restoring
      const lockedDeviceIds = new Set(
        restored
          .filter((a) => a.locallyAssigned || a.completed)
          .flatMap((a) => [a.selectedDeviceId, a.selectedDevice?.id])
          .filter(Boolean)
      );
      restored.forEach((a) => {
        if (a.locallyAssigned || a.completed) return;
        const devId = a.selectedDeviceId || a.selectedDevice?.id || null;
        if (devId && lockedDeviceIds.has(devId)) {
          a.selectedDeviceId = null;
          a.selectedDevice = null;
        }
      });

      this.deviceAssignments = restored;
      return true;
    },

    clearLocalStorage() {
      const key = this.localStorageKey();
      if (key) localStorage.removeItem(key);
    },

    // ── Complete task ─────────────────────────────────────────────────────────

    async completePreInstallTask() {
      if (!this.task) return;
      this.completing = true;

      try {
        const newlyAssignedRows = this.deviceAssignments.filter(
          (a) => a.locallyAssigned
        );
        const allAssignedRows = this.deviceAssignments.filter(
          (a) => a.locallyAssigned || a.completed
        );

        // Move each new device to its unit
        for (const assignment of newlyAssignedRows) {
          await this.$apollo.mutate({
            mutation: addOrganizationDevices,
            variables: {
              id: assignment.selectedUnitId,
              devices: [assignment.selectedDevice.id],
            },
          });
        }

        // Build checklist preserving server IDs
        const existingChecklist = Array.isArray(this.task.actions?.checklist)
          ? this.task.actions.checklist
          : [];

        const checklistActions = existingChecklist.map((item, idx) => {
          const assignment = this.deviceAssignments[idx];
          if (assignment && assignment.locallyAssigned) {
            return {
              id: item.id,
              completed: true,
              comment: assignment.comment || "",
              description: `${this.formatDeviceName(
                assignment.selectedDevice
              )} → ${assignment.selectedUnit?.name || "Unit"}`,
            };
          }
          return {
            id: item.id,
            completed: item.completed || false,
            comment: item.comment || "",
            description: item.description || "",
          };
        });

        const assignmentsPayload = allAssignedRows.map((a) => ({
          deviceName: this.formatDeviceName(a.selectedDevice),
          unitName: a.selectedUnit?.name || `Unit ${a.selectedUnitId}`,
          comment: a.comment || "",
        }));

        await this.$apollo.mutate({
          mutation: UPDATE_TASK,
          variables: {
            id: this.task.id,
            assigneeId: this.task.assignee.id,
            description: this.task.description,
            dueAt: this.task.dueAt,
            organizationId: this.task.organization.id,
            priority: this.task.priority,
            resolution: "done",
            taskTypeId: this.task.taskType.id,
            title: this.task.title,
            actions: {
              checklist: checklistActions,
              assignments: assignmentsPayload,
            },
          },
        });

        this.clearLocalStorage();

        this.summaryAssignments = allAssignedRows.map((a) => ({
          deviceName: this.formatDeviceName(a.selectedDevice),
          unitName: a.selectedUnit?.name || `Unit ${a.selectedUnitId}`,
        }));

        this.currentScreen = "summary";
      } catch (err) {
        console.error("Error completing pre-install task:", err);
        if (err.graphQLErrors?.length) {
          err.graphQLErrors.forEach((e) => this.showErrorMessage(e.message));
        } else {
          this.showErrorMessage(
            err.message || "Failed to complete task. Please try again."
          );
        }
      } finally {
        this.completing = false;
      }
    },

    // ── Email notification ────────────────────────────────────────────────────

    async sendSummaryNotification() {
      this.sendingNotification = true;
      try {
        await this.$apollo.mutate({
          mutation: SEND_PREINSTALLED_DEVICE_MOBILE_SUMMARY,
          variables: {
            doneBy: this.doneByInput.trim(),
            facilityId: this.task.organization.id,
            emails: [...this.emailList],
            devicesDetails: this.summaryAssignments,
          },
        });
        this.showSuccessMessage("Notification sent successfully!");
        this.emailList = [];
        this.emailInput = "";
        this.doneByInput = "";
      } catch (err) {
        console.error("Error sending notification:", err);
        this.showErrorMessage("Failed to send notification. Please try again.");
      } finally {
        this.sendingNotification = false;
      }
    },

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());
    },

    addEmail() {
      const email = this.emailInput.trim();
      if (!email || !this.isValidEmail(email)) return;
      if (!this.emailList.includes(email)) this.emailList.push(email);
      this.emailInput = "";
    },

    removeEmail(index) {
      this.emailList.splice(index, 1);
    },

    // ── Finish / close ────────────────────────────────────────────────────────

    finishAndClose() {
      window.close();
      setTimeout(() => {
        window.location.href = "/";
      }, 1000);
    },

    // ── QR Scanner ───────────────────────────────────────────────────────────

    openScanner(index) {
      this.scanningForIndex = index;
      this.manualScanDeviceId = "";
      this.scannerOpen = true;
    },

    closeScanner() {
      this.scannerOpen = false;
      this.scanningForIndex = null;
      this.manualScanDeviceId = "";
    },

    onDeviceScanned(deviceData) {
      const device = this.availableDevices.find(
        (d) => d.externalId === deviceData.externalId || d.id === deviceData.id
      );
      if (device) {
        this.onScanDeviceFound(device);
      } else {
        this.showErrorMessage("Device not found in office inventory");
      }
    },

    onQRDetected(qrData) {
      const match = (qrData.text || "").match(/([A-Z0-9-]+)/);
      const deviceId = match ? match[1] : null;
      if (deviceId) this.searchScanDeviceByShortId(deviceId);
    },

    searchManualScanDevice() {
      const id = this.manualScanDeviceId.trim();
      if (!id) {
        this.showErrorMessage("Please enter a device ID");
        return;
      }
      this.searchScanDeviceByShortId(id);
    },

    searchScanDeviceByShortId(deviceId) {
      const device = this.availableDevices.find(
        (d) =>
          d.externalId?.toLowerCase().includes(deviceId.toLowerCase()) ||
          d.shortId?.toLowerCase() === deviceId.toLowerCase() ||
          d.id === deviceId
      );
      if (device) {
        this.onScanDeviceFound(device);
      } else {
        this.showErrorMessage(
          `Device "${deviceId}" not found in office inventory`
        );
      }
    },

    onScanDeviceFound(device) {
      const idx = this.scanningForIndex;
      if (idx === null || idx === undefined) return;

      const assignment = this.deviceAssignments[idx];
      if (!assignment) return;

      // Validate the scanned device is actually available for this row
      const available = this.availableDevicesForAssignment(assignment);
      if (!available.some((d) => d.id === device.id)) {
        this.showErrorMessage(
          `Device ${this.formatDeviceName(
            device
          )} is already assigned or not available`
        );
        return;
      }

      assignment.selectedDeviceId = device.id;
      assignment.selectedDevice = device;
      assignment.locallyAssigned = false;
      this.persistToLocalStorage();
      this.closeScanner();
      this.showSuccessMessage(
        `Device ${this.formatDeviceName(device)} scanned successfully`
      );
    },

    // ── Snackbar helpers ──────────────────────────────────────────────────────

    showSuccessMessage(msg) {
      this.snackbarMessage = msg;
      this.snackbarColor = "success";
      this.snackbarTimeout = 4000;
      this.showSnackbar = true;
    },

    showErrorMessage(msg) {
      this.snackbarMessage = msg;
      this.snackbarColor = "error";
      this.snackbarTimeout = 6000;
      this.showSnackbar = true;
    },
  },
};
</script>

<style scoped>
.scanner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000;
  z-index: 999;
  display: flex;
  flex-direction: column;
}

.scanner-header {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(0, 0, 0, 0.85);
  padding: 12px 16px;
  color: white;
  font-size: 16px;
  font-weight: 500;
  flex-shrink: 0;
}

.scanner-body {
  flex: 1;
  overflow: hidden;
}

.scanner-manual-entry {
  background: rgba(0, 0, 0, 0.85);
  flex-shrink: 0;
}

.mobile-preinstall {
  padding: 0;
  background-color: #f5f5f5;
  min-height: 100vh;
  min-width: 95vw;
}

.screen-container {
  padding: 0;
  width: 100%;
  min-height: 100vh;
}

/* ── Task header ── */
.task-header {
  background: white;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.task-title {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 6px 0;
  color: #1976d2;
}

.task-meta {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.due-date {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.task-description {
  font-size: 14px;
  color: #555;
  margin-top: 6px;
}

/* ── Loading / error ── */
.loading-container,
.error-container {
  padding: 20px;
}

/* ── Assignments container ── */
.assignments-container {
  padding: 16px;
}

.progress-header {
  background: white;
  padding: 14px;
  border-radius: 10px;
  margin-bottom: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.progress-label {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.progress-text {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

/* ── Assignment cards ── */
.assignment-cards {
  margin-bottom: 12px;
}

.assignment-card {
  background: white;
  border-radius: 10px;
  margin-bottom: 10px;
  padding: 14px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border-left: 4px solid #e0e0e0;
  transition: border-color 0.2s ease;
}

.assignment-card.card-completed {
  border-left-color: #4caf50;
  background-color: #f9fef9;
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 8px;
}

.card-number {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #1976d2;
  color: white;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.assignment-display {
  padding: 4px 0;
}

.assignment-pair {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
}

.pair-item {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.pair-label {
  font-size: 12px;
  color: #888;
  margin-right: 4px;
  white-space: nowrap;
}

.pair-value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.arrow-icon {
  flex-shrink: 0;
}

.assignment-comment {
  font-size: 13px;
  color: #777;
  margin-top: 4px;
}

.assignment-inputs {
  margin-top: 4px;
}

.comment-row {
  margin-top: 8px;
}

/* ── Add row ── */
.add-row-section {
  margin-bottom: 16px;
}

/* ── Action button ── */
.action-container {
  position: sticky;
  bottom: 16px;
  z-index: 10;
}

.action-container .v-btn {
  height: 52px;
  font-size: 16px;
  font-weight: 600;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* ── Completion header ── */
.completion-header {
  text-align: center;
  padding: 50px 20px 30px 20px;
  background: linear-gradient(135deg, #f5f9f5 0%, #e8f5e8 100%);
}

.completion-header h2 {
  font-size: 26px;
  font-weight: 700;
  color: #2e7d32;
  margin: 14px 0 8px 0;
}

.completion-subtitle {
  font-size: 15px;
  color: #4a4a4a;
  margin: 0;
}

.completion-summary {
  padding-bottom: 30px;
}

.completion-summary .v-btn {
  height: 52px;
  font-size: 16px;
  font-weight: 600;
  text-transform: none;
}

@media (max-width: 768px) {
  .task-header {
    padding: 16px;
  }

  .task-title {
    font-size: 19px;
  }

  .assignments-container {
    padding: 12px;
  }

  .assignment-card {
    padding: 12px;
  }

  .pair-value {
    font-size: 13px;
  }

  .completion-header {
    padding: 36px 16px 24px 16px;
  }

  .completion-header h2 {
    font-size: 22px;
  }
}
</style>
